import React from 'react';
import { WizardStepProps, DYNAMIC_VARIABLES } from '../../../types';

const ReviewAndConfirmationStep: React.FC<WizardStepProps> = ({
  state,
  updateState,
  errors
}) => {
  // EPOCH 63: Removed service tier display, cost calculation, and timeline estimation
  // Users don't need pricing reminders during campaign creation

  const getLeadSourceDisplay = () => {
    switch (state.lead_acquisition_method) {
      case 'preset_filters':
        return {
          name: 'Target Leads',
          description: `${state.selected_preset_filters?.length || 0} filter(s) selected`,
          icon: '🎯'
        };
      case 'csv_upload':
        return {
          name: 'CSV Upload',
          description: state.csv_file ? state.csv_file.name : 'File uploaded',
          icon: '📊'
        };
      case 'existing':
        return {
          name: 'Existing Lists',
          description: `${state.selected_existing_lists?.length || 0} list(s) selected`,
          icon: '📋'
        };
      case 'lead_generator':
        return {
          name: 'Lead Generator',
          description: 'Custom lead generation filters',
          icon: '⚡'
        };
      case 'past_list':
        return {
          name: 'Previous Target Lead List',
          description: state.selected_list_name || 'Selected from previous requests',
          icon: '📁'
        };
      default:
        return { name: 'Unknown', description: '', icon: '❓' };
    }
  };

  const getVariableUsage = () => {
    const usage: Record<string, number> = {};
    state.messages.forEach(msg => {
      DYNAMIC_VARIABLES.forEach(variable => {
        const count = (msg.content.match(new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
        if (count > 0) {
          usage[variable] = (usage[variable] || 0) + count;
        }
      });
    });
    return usage;
  };

  // No confirmation checks required - validation handled by parent

  // EPOCH 63: Removed serviceTier - no longer displayed in Campaign Overview
  const leadSource = getLeadSourceDisplay();
  const variableUsage = getVariableUsage();
  const totalCharacters = state.messages.reduce((total, msg) => total + msg.character_count, 0);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Confirm Campaign</h2>
        <p className="text-gray-600">
          Review all campaign details before launching your LinkedIn outreach
        </p>
      </div>

      {/* Campaign Overview */}
      {/* EPOCH 63: Removed Service Tier display - users don't need pricing reminder here */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Campaign Overview</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{state.campaign_name}</div>
            <div className="text-sm text-gray-600 mt-1">Campaign Name</div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{leadSource.name}</div>
            <div className="text-sm text-gray-600 mt-1">Lead Source</div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{state.messages.length}</div>
            <div className="text-sm text-gray-600 mt-1">Messages</div>
          </div>
        </div>
      </div>

      {/* Message Sequence Review */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Message Sequence: {state.sequence_name}</h3>
          <div className="text-sm text-gray-500">
            {totalCharacters} total characters • {Object.keys(variableUsage).length} variables used
          </div>
        </div>

        <div className="space-y-4">
          {state.messages.map((message, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                    {message.message_number}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    Message {message.message_number}
                  </span>
                  {index > 0 && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Send after {message.delay_days} days
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">{message.character_count} chars</span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <div className="text-sm text-gray-900 whitespace-pre-wrap">{message.content}</div>
              </div>
              
              {/* Sample Preview */}
              <div className="border-l-2 border-blue-200 pl-3">
                <div className="text-xs text-gray-600 mb-1">Preview with sample data:</div>
                <div className="text-sm text-gray-700">
                  {message.content
                    .replace(/%%first_name%%/g, 'John')
                    .replace(/%%last_name%%/g, 'Smith')
                    .replace(/%%company%%/g, 'TechCorp')
                    .replace(/%%position%%/g, 'VP of Sales')
                    .replace(/%%city%%/g, 'San Francisco')
                    .replace(/%%industry%%/g, 'Technology')
                    .replace(/%%topskill%%/g, 'Sales Leadership')
                    .replace(/%%mutualconnectionname%%/g, 'Sarah Johnson')
                    .replace(/%%timeincurrentrole%%/g, '2 years')
                    .replace(/%%linkedinurl%%/g, 'linkedin.com/in/johnsmith')
                    .replace(/%%customvariable%%/g, 'Custom Value')
                  }
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Variable Usage Summary */}
      {Object.keys(variableUsage).length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Personalization Variables</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(variableUsage).map(([variable, count]) => (
              <div key={variable} className="text-center">
                <code className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  {variable}
                </code>
                <div className="text-xs text-gray-500 mt-1">Used {count} times</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Chatbot Configuration */}
      {state.service_tier === 'top_qualifier' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Chatbot Configuration</h3>
          
          <div className="flex items-center space-x-4 mb-4">
            <input
              type="checkbox"
              id="ai-chatbot-enabled"
              checked={state.ai_chatbot_enabled || false}
              onChange={(e) => updateState({ ai_chatbot_enabled: e.target.checked })}
              className="h-4 w-4 text-purple-600 rounded border-gray-300"
            />
            <label htmlFor="ai-chatbot-enabled" className="text-sm font-medium text-gray-700">
              Enable AI-powered response handling (Top Qualifier feature)
            </label>
          </div>
          
          {state.ai_chatbot_enabled && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-sm text-purple-800">
                <p className="mb-2">🤖 <strong>AI Chatbot Features:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-purple-700">
                  <li>Automatic response to common questions</li>
                  <li>Lead qualification and scoring</li>
                  <li>Meeting scheduling integration</li>
                  <li>Personalized follow-up suggestions</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Template Save Confirmation */}
      {state.save_as_template && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <div className="text-sm font-medium text-green-800">
                Template will be saved: "{state.template_metadata?.template_name || state.sequence_name}"
              </div>
              {state.template_metadata?.template_description && (
                <div className="text-sm text-green-700 mt-1">{state.template_metadata.template_description}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Campaign Submission Options */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Completion Options</h3>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <input
              type="radio"
              id="save-draft"
              name="submission-type"
              checked={!state.submit_for_review}
              onChange={() => updateState({ submit_for_review: false })}
              className="mt-1 h-4 w-4 text-blue-600 border-gray-300"
            />
            <div className="flex-1">
              <label htmlFor="save-draft" className="text-sm font-medium text-gray-900 cursor-pointer">
                Save as Draft
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Save your campaign for later. You can continue editing and submit for review when ready.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="radio"
              id="submit-review"
              name="submission-type"
              checked={state.submit_for_review || false}
              onChange={() => updateState({ submit_for_review: true })}
              className="mt-1 h-4 w-4 text-green-600 border-gray-300"
            />
            <div className="flex-1">
              <label htmlFor="submit-review" className="text-sm font-medium text-gray-900 cursor-pointer">
                Submit for Review
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Submit your campaign to your assigned CSR for approval and launch preparation.
              </p>
              {state.submit_for_review && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-green-800">
                      <p className="font-medium mb-1">What happens next:</p>
                      <ul className="list-disc list-inside space-y-1 text-green-700">
                        <li>Your assigned CSR will be notified</li>
                        <li>They will review and approve your campaign</li>
                        <li>Once approved, your campaign will be locked and ready to launch</li>
                        <li>You'll be able to track progress in your campaigns dashboard</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {errors && Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-700">
            {Object.entries(errors).map(([field, fieldErrors]) => (
              <div key={field}>
                {fieldErrors.map((error, index) => (
                  <p key={index}>{error}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default ReviewAndConfirmationStep;